import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import { toast } from 'react-toastify';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import { DkpLocationModel, RpjLocationModel } from 'app/edozvola/cases/case-location/model/case-location.model';
import {
  deleteDkpLocation,
  deleteRpjLocation,
  fetchDkpLocations,
  fetchIspuLocation,
  fetchRpjLocations,
  getDkpLocation,
  getLocationDataSource,
  getRpjLocation,
  saveDkpLocation,
  saveRpjLocation,
} from 'app/edozvola/cases/case-location/service/case-location.service';
import success = toast.success;
import error = toast.error;
import info = toast.info;
import {ERRORS, messages, TABLE_QUERY_KEYS} from "app/config/constants";

export const fetchLocationDataSourceQuery = (idCase: string) => {
  return useQuery({
    queryKey: ['location-data-source', idCase],
    queryFn: () => getLocationDataSource(idCase),
  });
};

export const saveRpjLocationQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (rpjLocation: RpjLocationModel) => {
      return saveRpjLocation(idCase, rpjLocation);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.RPJ_LOCATIONS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchRpjLocationsQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.RPJ_LOCATIONS_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchRpjLocations(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const fetchRpjLocationQuery = (idRpjLocation: string) => {
  return useQuery({
    queryKey: ['rpj-location', idRpjLocation],
    queryFn: () => getRpjLocation(idRpjLocation),
    enabled: !!idRpjLocation,
  });
};

export const deleteRpjLocationQuery = () => {
  return useMutation({
    mutationFn: (idRpjLocation: number) => deleteRpjLocation(idRpjLocation),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.RPJ_LOCATIONS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const saveDkpLocationQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (dkpLocation: DkpLocationModel) => {
      return saveDkpLocation(idCase, dkpLocation);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.DKP_LOCATIONS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchDkpLocationsQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.DKP_LOCATIONS_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchDkpLocations(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const fetchDkpLocationQuery = (idDkpLocation: string) => {
  return useQuery({
    queryKey: ['dkp-location', idDkpLocation],
    queryFn: () => getDkpLocation(idDkpLocation),
    enabled: !!idDkpLocation,
  });
};

export const deleteDkpLocationQuery = () => {
  return useMutation({
    mutationFn: (idDkpLocation: number) => deleteDkpLocation(idDkpLocation),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.DKP_LOCATIONS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchIspuLocationQuery = (idCase: string, onSuccessCallback?: () => void) => {
  return useMutation({
    mutationFn: () => fetchIspuLocation(idCase),
    onSuccess: (result: { hasData: boolean; parcelsTruncated: boolean; errorMessage?: string }) => {
      if (result.errorMessage) {
        showToast(error, `Nije moguće dohvatiti podatke! ${result.errorMessage}`);
        return;
      }
      if (result.hasData) {
        showToast(
          success,
          result.parcelsTruncated
            ? 'Lokacija sadrži više čestica te je spremljeno prvih 50 čestica!'
            : 'Podaci o lokaciji zahvata dohvaćeni iz ISPU servisa'
        );
        queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.RPJ_LOCATIONS_TABLE] });
        queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.DKP_LOCATIONS_TABLE] });
        queryClient.invalidateQueries({ queryKey: ['location-data-source', idCase] });
        onSuccessCallback?.();
      } else {
        showToast(info, 'Servis nije vratio informacije o lokaciji zahvata');
      }
    },
    onError: () => {
      showToast(error, ERRORS.LOCATION_SERVICE_NOT_WORKING_ERROR);
    },
  });
};
